import { ReactComponent as Battery } from "./battery-storage.svg";
import { ReactComponent as Biomass } from "./biomass.svg";
import { ReactComponent as Coal } from "./coal.svg";
import { ReactComponent as Gas } from "./gas.svg";
import { ReactComponent as Generator } from "./generator.svg";
import { ReactComponent as Hybrid } from "./hybrid.svg";
import { ReactComponent as Hydro } from "./hydro.svg";
import { ReactComponent as Nuclear } from "./nuclear.svg";
import { ReactComponent as Oil } from "./oil.svg";
import { ReactComponent as Solar } from "./solar.svg";
import { ReactComponent as Wind } from "./wind.svg";

const ICONS = {
    battery: Battery,
    biomass: Biomass,
    coal: Coal,
    gas: Gas,
    generator: Generator,
    hybrid: Hybrid,
    hydro: Hydro,
    nuclear: Nuclear,
    oil: Oil,
    solar: Solar,
    wind: Wind
} as const;

const COLORS: Record<keyof typeof ICONS, string> = {
    battery: "#10b981", // green
    biomass: "#22c55e", // light green
    coal: "#475569", // slate
    gas: "#ef4444", // red
    generator: "#4b5563", // gray (default fallback)
    hybrid: "#8b5cf6", // violet
    hydro: "#0ea5e9", // sky
    nuclear: "#06b6d4", // cyan
    oil: "#7c2d12", // brown
    solar: "#f59e0b", // amber
    wind: "#3b82f6" // blue
};

export type FuelTypeKey = keyof typeof ICONS;

// Fuel type enums vary by region and may evolve over time, so we use a
// heuristic to determine which icon to show based on the presence of certain
// keywords in the fuel type string. Always returns a key (never undefined) —
// unrecognized or missing fuel-type strings fall back to the generic
// "generator" icon so new ISOs / new fuel-type values never render blank.
export const determineFuelType = (
    rawType: string | null | undefined
): FuelTypeKey => {
    if (!rawType) return "generator";
    const lower = rawType.toLowerCase();

    // PJM uses ";", SPP uses "/", MISO occasionally uses ",". Multi-fuel
    // strings get tokenized to detect whether ≥2 distinct fuel categories
    // are present — those render as hybrid regardless of whether the literal
    // word "hybrid" appears.
    const tokens = lower
        .split(/[,;/]+/)
        .map((t) => t.trim())
        .filter(Boolean);
    const has = (re: RegExp) => tokens.some((t) => re.test(t));

    const flags: Record<
        Exclude<FuelTypeKey, "generator" | "hybrid">,
        boolean
    > = {
        solar: has(/solar|photovoltaic|\bpv\b/),
        wind: has(/wind/),
        battery: has(/battery|storage|bess/),
        nuclear: has(/nuclear|fission|fusion/),
        hydro: has(/hydro/),
        gas: has(
            // \bgas\b prevents false-matching "gas" inside "biogas" (which
            // should classify as biomass, not hybrid).
            /\bgas\b|combined cycle|combustion|natural|waste heat|\bct\b|\bcc\b/
        ),
        coal: has(/coal/),
        oil: has(/oil|diesel/),
        biomass: has(/biomass|biogas/)
    };

    // Display priority: renewables first, then conventional fuels.
    const order: Array<keyof typeof flags> = [
        "solar",
        "wind",
        "battery",
        "nuclear",
        "hydro",
        "gas",
        "coal",
        "oil",
        "biomass"
    ];
    const detected = order.filter((k) => flags[k]);

    if (lower.includes("hybrid") || detected.length >= 2) return "hybrid";

    if (detected.length === 1) return detected[0];

    // Catch-all for "Unknown", "Other", "Not Applicable", or any future
    // fuel-type value we have not mapped yet.
    return "generator";
};

const SQUARE_BORDER_RADIUS_PX = 4;

// Matches Blueprint's $pt-text-color-muted so plain icons read as
// secondary content alongside muted text.
const PLAIN_ICON_COLOR = "#5f6b7c";

/** The bare svg and accent colour for a fuel type, without the round chip. */
export const getFuelTypeGlyph = (fuelType: string | null | undefined) => {
    const key = determineFuelType(fuelType);
    return { Icon: ICONS[key], color: COLORS[key] };
};

interface FuelTypeIconProps {
    fuelType: string | null | undefined;
    size?: number;
    /** Badge shape. Defaults to the original circular badge. */
    shape?: "circle" | "square";
    /**
     * "badge" (default) renders the icon in a colored tinted chip;
     * "plain" renders just the icon in muted gray with no background.
     */
    variant?: "badge" | "plain";
}

export const FuelTypeIcon: React.FC<FuelTypeIconProps> = ({
    fuelType,
    size = 16,
    shape = "circle",
    variant = "badge"
}) => {
    const key = determineFuelType(fuelType);
    const Icon = ICONS[key];
    const color = COLORS[key];
    const padding = Math.max(3, Math.round(size * 0.25));

    if (variant === "plain") {
        return (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: PLAIN_ICON_COLOR
                }}
            >
                <Icon width={size} height={size} />
            </span>
        );
    }

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding,
                backgroundColor: `${color}26`,
                borderRadius:
                    shape === "circle" ? "50%" : SQUARE_BORDER_RADIUS_PX,
                color
            }}
        >
            <Icon width={size} height={size} />
        </span>
    );
};
